/**
 * Login page for evo-infinito
 * Styled with Evolutivo branding
 */

import React from "react";
import { useLogin } from "@refinedev/core";
import { Card, Form, Input, Button, Typography, Space, Alert } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

interface LoginFormValues {
  username: string;
  access_key: string;
}

export const LoginPage: React.FC = () => {
  const { mutate: login, isLoading, error } = useLogin<LoginFormValues>();

  const onFinish = (values: LoginFormValues) => {
    login(values);
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background:
          "linear-gradient(135deg, #282a64 0%, #3a3c78 50%, #282a64 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative accent */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "60%",
          height: "150%",
          background:
            "linear-gradient(180deg, rgba(245, 117, 38, 0.15) 0%, rgba(245, 117, 38, 0.05) 100%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Card
        style={{
          width: 420,
          boxShadow: "0 16px 48px rgba(40, 42, 100, 0.3)",
          border: "none",
          borderRadius: 16,
          position: "relative",
          zIndex: 1,
        }}
        styles={{
          body: { padding: "40px 36px" },
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Logo and Title */}
          <div style={{ textAlign: "center", marginBottom: 8 }}>
            <img
              src="/logo.png"
              alt="evo-infinito"
              style={{
                height: 72,
                width: "auto",
                marginBottom: 16,
              }}
            />
            <Title
              level={3}
              style={{
                margin: 0,
                color: "#282a64",
                fontWeight: 600,
                letterSpacing: "-0.5px",
              }}
            >
              evo-infinito
            </Title>
            <Text
              style={{
                color: "#5c5e8a",
                fontSize: 14,
              }}
            >
              Sign in to your account
            </Text>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert
              message="Login Failed"
              description={
                (error as any)?.message || "Invalid username or access key"
              }
              type="error"
              showIcon
              style={{ borderRadius: 8 }}
            />
          )}

          {/* Login Form */}
          <Form<LoginFormValues>
            layout="vertical"
            onFinish={onFinish}
            requiredMark={false}
          >
            <Form.Item
              name="username"
              label={
                <span style={{ color: "#282a64", fontWeight: 500 }}>
                  Username
                </span>
              }
              rules={[
                { required: true, message: "Please enter your username" },
              ]}
            >
              <Input
                prefix={<UserOutlined style={{ color: "#8b8db0" }} />}
                placeholder="admin"
                size="large"
                style={{
                  borderRadius: 8,
                  borderColor: "#e2e4ef",
                }}
              />
            </Form.Item>

            <Form.Item
              name="access_key"
              label={
                <span style={{ color: "#282a64", fontWeight: 500 }}>
                  Access Key
                </span>
              }
              rules={[
                { required: true, message: "Please enter your access key" },
              ]}
              extra={
                <Text style={{ fontSize: 12, color: "#8b8db0" }}>
                  Find your access key in My Preferences → Access Key
                </Text>
              }
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: "#8b8db0" }} />}
                placeholder="Your access key"
                size="large"
                style={{
                  borderRadius: 8,
                  borderColor: "#e2e4ef",
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                size="large"
                style={{
                  height: 48,
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 15,
                  background: "#282a64",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(40, 42, 100, 0.3)",
                }}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>

          {/* Footer */}
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <Text style={{ fontSize: 12, color: "#8b8db0" }}>
              Powered by{" "}
              <a
                href="https://www.evolutivo.it"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#f57526", fontWeight: 500 }}
              >
                Evolutivo
              </a>
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default LoginPage;
